import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import UnderlineExtension from '@tiptap/extension-underline';
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo, Redo, RemoveFormatting, Palette, Highlighter, Ban } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { Separator } from './ui/separator';

// Highlight via inline background-color op textStyle (e-mailclient-vriendelijk)
const BackgroundColor = Extension.create({
  name: 'backgroundColor',
  addGlobalAttributes() {
    return [{
      types: ['textStyle'],
      attributes: {
        backgroundColor: {
          default: null,
          parseHTML: (element) => element.style.backgroundColor || null,
          renderHTML: (attributes) => {
            if (!attributes.backgroundColor) return {};
            return { style: `background-color: ${attributes.backgroundColor}` };
          },
        },
      },
    }];
  },
});

const TEXT_COLORS = [
  { c: '#f97316', t: 'Oranje' },
  { c: '#dc2626', t: 'Rood' },
  { c: '#16a34a', t: 'Groen' },
  { c: '#2563eb', t: 'Blauw' },
  { c: '#9333ea', t: 'Paars' },
  { c: '#ca8a04', t: 'Goud' },
  { c: '#6b7280', t: 'Grijs' },
  { c: '#000000', t: 'Zwart' },
];

const HIGHLIGHT_COLORS = [
  { c: '#fef08a', t: 'Geel' },
  { c: '#bbf7d0', t: 'Groen' },
  { c: '#bfdbfe', t: 'Blauw' },
  { c: '#fecaca', t: 'Rood' },
  { c: '#fed7aa', t: 'Oranje' },
];

const MenuButton = ({ onClick, isActive, children, title, disabled = false }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "h-8 w-8 p-0 hover:bg-primary/10",
      isActive && "bg-primary/20 text-primary"
    )}
    title={title}
  >
    {children}
  </Button>
);

// Garandeer oranje links in de opgeslagen HTML (huisstijl)
const ensureLinkColor = (html) => (html || '')
  .replace(/<a(?![^>]*style=)/g, '<a style="color: #f97316; text-decoration: underline;"')
  .replace(/<a\s([^>]*?)style="(?![^"]*color\s*:)([^"]*)"/g, '<a $1style="color: #f97316; $2"');

const HtmlEditor = ({ value, onChange, placeholder, className = '', minHeight = '200px' }) => {
  const [showColorMenu, setShowColorMenu] = React.useState(false);
  const [showHighlightMenu, setShowHighlightMenu] = React.useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        underline: false,
      }),
      Link.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: (el) => el.getAttribute('style'),
              renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
            },
          };
        },
      }).configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      BackgroundColor,
      UnderlineExtension,
    ],
    content: ensureLinkColor(value || ''),
    onUpdate: ({ editor }) => {
      onChange(ensureLinkColor(editor.getHTML()));
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none px-4 py-3',
        style: `min-height: ${minHeight}`,
      },
    },
  });

  // Update content when value prop changes externally
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(ensureLinkColor(value || ''));
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url, style: 'color: #f97316; text-decoration: underline;' }).run();
    }
  };

  return (
    <div className={cn("border rounded-lg overflow-hidden bg-background", className)}>
      {/* Professional Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b bg-muted/50">
        {/* Text Formatting */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Vet (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Cursief (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Onderstrepen (Ctrl+U)"
        >
          <Underline className="h-4 w-4" />
        </MenuButton>
        
        <Separator orientation="vertical" className="mx-1 h-6" />
        
        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Opsomming"
        >
          <List className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Genummerde lijst"
        >
          <ListOrdered className="h-4 w-4" />
        </MenuButton>
        
        <Separator orientation="vertical" className="mx-1 h-6" />
        
        {/* Alignment */}
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Links uitlijnen"
        >
          <AlignLeft className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Centreren"
        >
          <AlignCenter className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Rechts uitlijnen"
        >
          <AlignRight className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          isActive={editor.isActive({ textAlign: 'justify' })}
          title="Uitvullen"
        >
          <AlignJustify className="h-4 w-4" />
        </MenuButton>
        
        <Separator orientation="vertical" className="mx-1 h-6" />
        
        {/* Tekstkleur */}
        <div className="relative">
          <MenuButton
            onClick={() => { setShowColorMenu(v => !v); setShowHighlightMenu(false); }}
            isActive={!!editor.getAttributes('textStyle').color}
            title="Tekstkleur"
          >
            <Palette className="h-4 w-4" style={{ color: editor.getAttributes('textStyle').color || undefined }} />
          </MenuButton>
          {showColorMenu && (
            <div className="absolute z-50 top-9 left-0 bg-popover border rounded-md shadow-lg p-2 flex flex-wrap gap-1 w-40" data-testid="editor-color-menu">
              {TEXT_COLORS.map(({ c, t }) => (
                <button
                  key={c}
                  type="button"
                  title={t}
                  onClick={() => { editor.chain().focus().setColor(c).run(); setShowColorMenu(false); }}
                  className="h-6 w-6 rounded border hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                  data-testid={`editor-color-${c.replace('#','')}`}
                />
              ))}
              <input
                type="color"
                title="Eigen kleur"
                onChange={(e) => { editor.chain().focus().setColor(e.target.value).run(); }}
                className="h-6 w-6 rounded border cursor-pointer p-0 bg-transparent"
                data-testid="editor-color-custom"
              />
              <button
                type="button"
                title="Standaardkleur"
                onClick={() => { editor.chain().focus().unsetColor().run(); setShowColorMenu(false); }}
                className="h-6 w-6 rounded border flex items-center justify-center text-muted-foreground hover:bg-muted"
                data-testid="editor-color-reset"
              >
                <Ban className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        
        {/* Markeren */}
        <div className="relative">
          <MenuButton
            onClick={() => { setShowHighlightMenu(v => !v); setShowColorMenu(false); }}
            isActive={!!editor.getAttributes('textStyle').backgroundColor}
            title="Tekst markeren"
          >
            <Highlighter className="h-4 w-4" />
          </MenuButton>
          {showHighlightMenu && (
            <div className="absolute z-50 top-9 left-0 bg-popover border rounded-md shadow-lg p-2 flex flex-wrap gap-1 w-32" data-testid="editor-highlight-menu">
              {HIGHLIGHT_COLORS.map(({ c, t }) => (
                <button
                  key={c}
                  type="button"
                  title={t}
                  onClick={() => { editor.chain().focus().setMark('textStyle', { backgroundColor: c }).run(); setShowHighlightMenu(false); }}
                  className="h-6 w-6 rounded border hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                  data-testid={`editor-highlight-${c.replace('#','')}`}
                />
              ))}
              <button
                type="button"
                title="Markering verwijderen"
                onClick={() => { editor.chain().focus().setMark('textStyle', { backgroundColor: null }).run(); setShowHighlightMenu(false); }}
                className="h-6 w-6 rounded border flex items-center justify-center text-muted-foreground hover:bg-muted"
                data-testid="editor-highlight-reset"
              >
                <Ban className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        
        <Separator orientation="vertical" className="mx-1 h-6" />
        
        {/* Link */}
        <MenuButton
          onClick={addLink}
          isActive={editor.isActive('link')}
          title="Link toevoegen"
        >
          <LinkIcon className="h-4 w-4" />
        </MenuButton>
        
        {/* Clear Formatting */}
        <MenuButton
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Opmaak verwijderen"
        >
          <RemoveFormatting className="h-4 w-4" />
        </MenuButton>
        
        <Separator orientation="vertical" className="mx-1 h-6" />
        
        {/* Undo/Redo */}
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Ongedaan maken (Ctrl+Z)"
        >
          <Undo className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Opnieuw (Ctrl+Y)"
        >
          <Redo className="h-4 w-4" />
        </MenuButton>
      </div>
      
      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="focus-within:ring-1 focus-within:ring-primary/20"
      />
      
      <style>{`
        .ProseMirror {
          min-height: ${minHeight};
          padding: 16px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          line-height: 1.6;
        }
        .ProseMirror p {
          margin: 0 0 0.75em 0;
        }
        .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror ul,
        .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror a {
          color: #ea580c;
          text-decoration: underline;
        }
        .ProseMirror u {
          text-decoration: underline;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #9ca3af;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default HtmlEditor;
